import { ArrowDownIcon } from '@solar-icons/react/linear/arrow-down'
import { ArrowUpIcon } from '@solar-icons/react/linear/arrow-up'
import { SortVerticalIcon } from '@solar-icons/react/linear/sort-vertical'
import type { QueryResultSet } from '@/lib/api/models'
import { formatCell } from '@/lib/format/cells'
import { formatColumnName } from '@/lib/format/columns'
import { cn } from '@/lib/utils/cn'
import { useLocale } from '@/lib/hooks/useLocale'
import { useSettingsStore } from '@/stores/settingsStore'
import { useResultTable } from './useResultTable'

export interface ResultTableProps {
  result: QueryResultSet
  /** Caps the visible height; the body scrolls past it. */
  maxHeight?: number
  className?: string
}

export function ResultTable({
  result,
  maxHeight = 420,
  className,
}: ResultTableProps) {
  const locale = useLocale()
  const density = useSettingsStore((state) => state.density)
  const { rows, numericColumns, sort, toggleSort } = useResultTable(result)

  if (!result.columns.length || !result.rows.length) return null

  const cellPadding = density === 'compact' ? 'px-3 py-1.5' : 'px-3.5 py-2.5'

  return (
    <div
      className={cn(
        'scrollbar-thin overflow-auto rounded-xl border border-line bg-surface',
        className,
      )}
      style={{ maxHeight }}
    >
      <table className="w-full border-collapse font-mono text-xs whitespace-nowrap">
        <thead>
          <tr>
            {result.columns.map((column, index) => {
              const isNumeric = numericColumns[index]
              const isSorted = sort?.columnIndex === index

              return (
                <th
                  key={`${column}-${index}`}
                  scope="col"
                  aria-sort={
                    isSorted
                      ? sort.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  className={cn(
                    'sticky top-0 z-10 border-b border-line bg-sunken',
                    cellPadding,
                    isNumeric ? 'text-right' : 'text-left',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(index)}
                    title={`Sort by ${formatColumnName(column)}`}
                    className={cn(
                      'group inline-flex items-center gap-1 text-[10px] font-semibold tracking-wider uppercase',
                      'transition-colors',
                      isNumeric && 'flex-row-reverse',
                      isSorted ? 'text-accent' : 'text-subtle hover:text-fg',
                    )}
                  >
                    {formatColumnName(column)}
                    {isSorted ? (
                      sort.direction === 'asc' ? (
                        <ArrowUpIcon size={12} strokeWidth={2.25} />
                      ) : (
                        <ArrowDownIcon size={12} strokeWidth={2.25} />
                      )
                    ) : (
                      <SortVerticalIcon
                        size={12}
                        strokeWidth={2}
                        className="opacity-0 transition-opacity group-hover:opacity-60"
                      />
                    )}
                  </button>
                </th>
              )
            })}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className="border-b border-line/60 transition-colors last:border-b-0 hover:bg-hover"
            >
              {result.columns.map((column, columnIndex) => (
                <td
                  key={columnIndex}
                  className={cn(
                    cellPadding,
                    numericColumns[columnIndex]
                      ? 'tabular text-right text-fg'
                      : 'text-left text-muted',
                    columnIndex === 0 && 'font-medium text-fg',
                  )}
                >
                  {formatCell(row[columnIndex] ?? null, { column, locale })}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
